import { Alert } from '@/global/components/Alert'
import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { Input } from '@/global/components/ui/input'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { CheckIcon, CopyIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
    ApiClientWithSecret,
    CreateApiClientInput,
    apiClients,
    createApiClientSchema,
} from 'service/api'
import { toast } from 'sonner'
import { CredencialForm } from '../atoms/CredencialForm'

export default function AdicionarCredencial() {
    const navigate = useNavigate()
    const [createdCredencial, setCreatedCredencial] =
        useState<ApiClientWithSecret | null>(null)
    const [copiedId, setCopiedId] = useState(false)
    const [copiedSecret, setCopiedSecret] = useState(false)

    const { mutateAsync, isLoading } = apiClients.mutations.useCreate({
        invalidateQuery: true,
    })

    const handleClose = () => {
        setCreatedCredencial(null)
        navigate('/integracoes/credenciais-api')
    }

    const handleSubmit: SubmitHandler<CreateApiClientInput> = async data => {
        try {
            const response = await mutateAsync(data)
            const created = response as unknown as ApiClientWithSecret
            setCreatedCredencial(created)
            toast.success('Credencial criada com sucesso')
        } catch (err: any) {
            toast.error(err?.message || 'Erro ao criar credencial')
        }
    }

    const copyToClipboard = async (text: string, type: 'id' | 'secret') => {
        try {
            await navigator.clipboard.writeText(text)
            if (type === 'id') {
                setCopiedId(true)
                setTimeout(() => setCopiedId(false), 2000)
            } else {
                setCopiedSecret(true)
                setTimeout(() => setCopiedSecret(false), 2000)
            }
            toast.success('Copiado para a área de transferência')
        } catch {
            toast.error('Erro ao copiar')
        }
    }

    if (createdCredencial) {
        return (
            <Dialog.Root open onClose={handleClose}>
                <Dialog.Title>Credencial criada com sucesso</Dialog.Title>
                <Dialog.Content>
                    <Alert severity="warning" className="mb-4">
                        <strong>Atenção:</strong> Este segredo será mostrado
                        apenas uma vez! Copie e guarde-o em um local seguro
                        agora. Se perder este valor, será necessário regenerar a
                        credencial.
                    </Alert>

                    <div className="flex flex-col gap-4">
                        <div>
                            <span className="text-xs font-semibold text-muted-foreground">
                                Nome da aplicação
                            </span>
                            <p className="text-sm font-medium text-foreground">
                                {createdCredencial.nome}
                            </p>
                        </div>

                        <Input
                            label="Client ID"
                            value={createdCredencial.clientId}
                            readOnly
                            endAdornment={
                                <IconButton
                                    onClick={() =>
                                        copyToClipboard(
                                            createdCredencial.clientId,
                                            'id',
                                        )
                                    }
                                    title="Copiar Client ID"
                                >
                                    {copiedId ? (
                                        <CheckIcon className="size-4 text-success" />
                                    ) : (
                                        <CopyIcon className="size-4" />
                                    )}
                                </IconButton>
                            }
                        />

                        <Input
                            label="Client Secret (segredo da API)"
                            value={createdCredencial.clientSecret}
                            readOnly
                            endAdornment={
                                <IconButton
                                    onClick={() =>
                                        copyToClipboard(
                                            createdCredencial.clientSecret,
                                            'secret',
                                        )
                                    }
                                    title="Copiar Client Secret"
                                >
                                    {copiedSecret ? (
                                        <CheckIcon className="size-4 text-success" />
                                    ) : (
                                        <CopyIcon className="size-4" />
                                    )}
                                </IconButton>
                            }
                        />
                    </div>
                </Dialog.Content>
                <Dialog.Actions>
                    <Button variant="contained" onClick={handleClose}>
                        Concluir
                    </Button>
                </Dialog.Actions>
            </Dialog.Root>
        )
    }

    return (
        <Dialog.Root
            loading={isLoading}
            onSubmit={handleSubmit}
            schema={createApiClientSchema}
            open={hasPermission(['create', 'CredenciaisDeAPI'])}
            onClose={handleClose}
        >
            <Dialog.Title>Nova credencial de API</Dialog.Title>
            <Dialog.Content>
                <CredencialForm />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton onClick={handleClose} />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
